<script setup lang="ts">
import { computed, useSlots, type VNode } from "vue";
import { BaseSelect, type BaseSelectOption } from "@lunagoai/vue-ui";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<{
  modelValue?: string | number | null;
  options?: BaseSelectOption[];
  id?: string;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  required?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptySearchText?: string;
  emptyOptionsText?: string;
}>(), {
  modelValue: "",
  disabled: false,
  required: false,
  searchable: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string | number | null];
  change: [value: string | number | null];
}>();

const slots = useSlots();

function flatten(nodes: VNode[]): VNode[] {
  return nodes.flatMap((node) => {
    if (Array.isArray(node.children)) return flatten(node.children as VNode[]);
    return [node];
  });
}

function textContent(node: VNode): string {
  if (typeof node.children === "string") return node.children.trim();
  if (Array.isArray(node.children)) return node.children.map((child) => typeof child === "string" ? child : textContent(child as VNode)).join("").trim();
  return "";
}

const slotOptions = computed<BaseSelectOption[]>(() => {
  const nodes = flatten(slots.default?.() ?? []);
  return nodes
    .filter((node) => node.type === "option")
    .map((node) => ({
      value: (node.props?.value ?? "") as string | number,
      label: textContent(node),
    }));
});

const resolvedOptions = computed(() => props.options ?? slotOptions.value);
</script>

<template>
  <BaseSelect
    v-bind="$attrs"
    :id="id"
    :model-value="modelValue"
    :options="resolvedOptions"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :placeholder="placeholder"
    :searchable="searchable"
    :search-placeholder="searchPlaceholder"
    :empty-search-text="emptySearchText"
    :empty-options-text="emptyOptionsText"
    @update:model-value="emit('update:modelValue', $event as string | number | null)"
    @change="emit('change', $event as string | number | null)"
  />
</template>
